<script setup lang="ts">
import type { IMainVideosItemDataClientDTO } from '~/shared/api/client/main-videos/main-videos.dto';
import { resizeCropSlot } from '~/shared/lib/resizeImage';
import { CROP_RATIO_VIDEO } from '~/shared/lib/crop-ratio.const';

const props = defineProps<
  IMainVideosItemDataClientDTO & {
    isImageOnTop?: boolean;
    textColor: string;
  }
>();

const imageOne = props.image_one?.file_url || undefined;
const imageAttrs = computed(() => {
  const useSecondImage = !props.isImageOnTop && !!props.image_two?.file_url;

  return resizeCropSlot(
    useSecondImage ? props.image_two?.file_url : imageOne,
    CROP_RATIO_VIDEO,
    useSecondImage ? 1 : 0,
    { width: props.isImageOnTop ? 770 : 320 },
  );
});

const emit = defineEmits<{
  (e: 'openPlayer', payload: { title: string; iframe: string }): void;
}>();

const openModal = () => {
  emit('openPlayer', { title: props.title, iframe: props.subject! });
};
</script>

<template>
  <div
    :class="[$style.item, { [$style.item_imageTop!]: props?.isImageOnTop }]"
    :style="{
      '--text-color': textColor,
    }"
  >
    <div :class="$style.item__info">
      <NuxtLink
        v-if="props.category_link && props.category"
        :class="$style.item__category"
        :to="props.category_link"
      >
        {{ props.category }}
      </NuxtLink>

      <div :class="$style.item__title" @click="openModal">
        {{ props.title }}
      </div>
    </div>
    <div v-if="imageOne" :class="$style.item__imageLink" @click="openModal">
      <img
        :class="$style.item__image"
        :src="imageAttrs.src"
        :srcset="imageAttrs.srcset"
        :alt="props.title"
      />
      <div :class="$style.item__image__iconWrapper">
        <Icon :class="$style.item__image__icon" name="my-icon:i-play" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.item {
  display: flex;
  flex-flow: column nowrap;
  padding: 20px;
  gap: var(--space-1-5x);
  justify-content: space-between;
  height: 100%;

  &_imageTop {
    flex-direction: column-reverse;

    .item__image__iconWrapper {
      width: 48px;
      height: 48px;
    }
  }

  &__info {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-1x);
  }

  &__category {
    @include text_caption2_caps;
    color: var(--color-gray-text);
    text-decoration: none;
  }

  &__title {
    @include text_body2;
    @include line_clamp(3);
    color: var(--text-color);
    text-decoration: none;
    cursor: pointer;
  }

  &__image {
    margin-top: auto;
    display: block;
    width: 100%;
    height: auto;

    &Link {
      cursor: pointer;
      position: relative;

      &::before {
        position: absolute;
        content: '';
        width: 100%;
        height: 100%;
        background: linear-gradient(
          0deg,
          rgba(0, 0, 0, 0.6) 0%,
          rgba(0, 0, 0, 0) 100%
        );
      }
    }

    &__icon {
      width: 20px;
      path {
        width: 20px;
        font-size: 20px;
        fill: var(--color-white);
      }

      &Wrapper {
        position: absolute;
        left: 20px;
        bottom: 20px;
        width: 32px;
        height: 32px;
        background-color: var(--color-red-1);
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 480px) {
  .item {
    &_imageTop {
      padding: var(--space-2x);
    }
    &_imageTop &__image {
      &Link {
        margin-left: -16px;
        margin-right: -16px;
      }
    }
  }
}
</style>
